import { credentialRoleArnValidator as credentialRoleArnZodParser, FilespaceFormat, filespaceNameValidator as filespaceNameZodParser, storageBucketValidator, StorageCredentialsType, storageEndpointValidator, StorageOwner, storageProviderIdValidator, storageRegionNameValidator, stringValidator, } from '@lucidlink/dto/validation';
import { z } from '@lucidlink/dto/z';
import { remapZodErrors } from '@lucidlink/uicore';
import { FILESPACE_CREATE_FORM_CASES, PRODUCTION_STORAGE_PROVIDER, } from '#constants';
export const storageAccessKeyIdValidator = stringValidator({
    minLength: {
        min: 1,
        error: 'filespace.storageAccessKeyId.required',
    },
});
export const storageSecretAccessKeyValidator = stringValidator({
    minLength: {
        min: 1,
        error: 'filespace.storageSecretAccessKey.required',
    },
});
export const validatorSchemaOrLiteralEmptyString = (schema) => {
    return schema.or(z.literal(''));
};
export const filespaceNameValidator = (value) => {
    const res = filespaceNameZodParser.safeParse(value);
    return {
        isValid: res.success,
        errors: res.success ? [] : remapZodErrors(res.error.issues),
    };
};
export const credentialsRoleArnValidator = (value) => {
    const res = credentialRoleArnZodParser.safeParse(value);
    return {
        isValid: res.success,
        errors: res.success ? [] : remapZodErrors(res.error.issues),
    };
};
const commonFilespaceCreateFormFields = {
    name: filespaceNameZodParser,
    filespaceFormat: z.nativeEnum(FilespaceFormat),
    subscriptionType: z.literal('custom'),
    storageBlockSize: z
        .string()
        .transform((val) => {
        if (!val) {
            return undefined;
        }
        const parsed = Number.parseInt(String(val), 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    })
        .optional(),
};
// The four cases we have for filespace create payload
// 1. LucidLink storage - production filespace
// 2. Owner storage - has storage regions
// 3. Owner storage - no storage regions
// 4. Owner storage - storageProviderId === STORAGE_PROVIDER_OTHER
export const filespaceCreateLucidLinkStorageFormParser = z.object({
    ...commonFilespaceCreateFormFields,
    storageOwner: z.literal(StorageOwner.LUCIDLINK),
    // Let's see if this is still valid
    storageProviderId: z.literal(PRODUCTION_STORAGE_PROVIDER),
    storageRegionId: z.string(),
});
export const filespaceCreateOwnStorageWithRegionsFormParser = z.object({
    ...commonFilespaceCreateFormFields,
    storageOwner: z.literal(StorageOwner.CUSTOMER),
    storageProviderId: storageProviderIdValidator,
    storageRegionId: z.string(),
    storageAccessKeyId: storageAccessKeyIdValidator,
    storageSecretAccessKey: storageSecretAccessKeyValidator,
    // Allows empty strings to pass the validation, as the field is optional
    storageBucket: validatorSchemaOrLiteralEmptyString(storageBucketValidator),
});
export const filespaceCreateOwnStorageWithRegionsAndCredentialsRoleFormParser = z.object({
    ...commonFilespaceCreateFormFields,
    storageOwner: z.literal(StorageOwner.CUSTOMER),
    storageProviderId: storageProviderIdValidator,
    storageRegionId: z.string(),
    // storageAccessKeyId / storageSecretAccessKey are intentionally omitted: the role flow never
    // collects them (no Credentials step), and z.object() strips them, so a stale value from a
    // previously selected provider can never leak into the payload.
    storageRoleArn: credentialRoleArnZodParser,
    storageCredentialsType: z.literal(StorageCredentialsType.ROLE),
    storageRoleExternalId: z.string(),
    storageBucket: storageBucketValidator,
});
export const filespaceCreateOwnStorageNoRegionsFormParser = z.object({
    ...commonFilespaceCreateFormFields,
    storageOwner: z.literal(StorageOwner.CUSTOMER),
    storageAccessKeyId: storageAccessKeyIdValidator,
    storageSecretAccessKey: storageSecretAccessKeyValidator,
    storageProviderId: storageProviderIdValidator,
    storageEndpoint: storageEndpointValidator,
    // Allows empty strings to pass the validation, as the field is optional
    storageRegionName: validatorSchemaOrLiteralEmptyString(storageRegionNameValidator),
    storageBucket: validatorSchemaOrLiteralEmptyString(storageBucketValidator),
});
export const filespaceCreateOwnStorageOtherProviderFormParser = z.object({
    ...commonFilespaceCreateFormFields,
    storageOwner: z.literal(StorageOwner.CUSTOMER),
    storageAccessKeyId: storageAccessKeyIdValidator,
    storageSecretAccessKey: storageSecretAccessKeyValidator,
    storageProviderId: storageProviderIdValidator,
    storageEndpoint: storageEndpointValidator,
    // Storage Region Name is required in this case for some reason
    storageRegionName: storageRegionNameValidator,
    // Allows empty strings to pass the validation, as the field is optional
    storageBucket: validatorSchemaOrLiteralEmptyString(storageBucketValidator),
});
export const FILESPACE_FORM_PARSERS_MAP = {
    [FILESPACE_CREATE_FORM_CASES.LucidlinkStorage]: filespaceCreateLucidLinkStorageFormParser,
    [FILESPACE_CREATE_FORM_CASES.CustomOther]: filespaceCreateOwnStorageOtherProviderFormParser,
    [FILESPACE_CREATE_FORM_CASES.CustomWithRegions]: filespaceCreateOwnStorageWithRegionsFormParser,
    [FILESPACE_CREATE_FORM_CASES.CustomWithRegionsAndRole]: filespaceCreateOwnStorageWithRegionsAndCredentialsRoleFormParser,
    [FILESPACE_CREATE_FORM_CASES.CustomNoRegions]: filespaceCreateOwnStorageNoRegionsFormParser,
};
// Hold the object keys that need to be removed from the payload if they're empty strings - ''
export const FILESPACE_CREATE_PAYLOAD_NULLABLE_PROPS_MAP = {
    [FILESPACE_CREATE_FORM_CASES.CustomOther]: ['storageBucket'],
    [FILESPACE_CREATE_FORM_CASES.CustomNoRegions]: [
        'storageBucket',
        'storageRegionName',
    ],
    [FILESPACE_CREATE_FORM_CASES.CustomWithRegions]: ['storageBucket'],
    [FILESPACE_CREATE_FORM_CASES.CustomWithRegionsAndRole]: [],
    [FILESPACE_CREATE_FORM_CASES.LucidlinkStorage]: [],
};
/**
 * Function sanitizes the payload by removing fields which have empty string as their property value. Needed, as
 * our BE will not accept empty strings or properties that have undefined as their value - the property key has to
 * be non existent in the object
 */
export const filespaceCreatePayloadSanitizer = (formData, formCase) => {
    function verifyPropertyValueIsEmptyString(value) {
        return typeof value === 'string' && value.length === 0;
    }
    const payload = { ...formData };
    const keysToSanitize = FILESPACE_CREATE_PAYLOAD_NULLABLE_PROPS_MAP[formCase];
    for (const key of keysToSanitize) {
        if (verifyPropertyValueIsEmptyString(payload[key])) {
            delete payload[key];
        }
    }
    return payload;
};
